import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import {
  DefectLegend,
  EquipmentDetailTypeEnum,
  OptionFieldTypeEnum,
} from '@/__generated__/graphql';

export const useGetDetailsData = (
  assemblyDetails: Record<string, string> | undefined,
  defectLegends: DefectLegend[] | undefined
) => {
  const assemblyTemplate = useAtomValue(state.assemblyTemplate);

  const templateList = useMemo(() => {
    return assemblyTemplate?.equipmentDetails?.filter(
      (detail) => detail.type === EquipmentDetailTypeEnum.TemplateList
    );
  }, [assemblyTemplate?.equipmentDetails]);

  const assemblyGeneralData = useMemo(() => {
    if (!assemblyDetails) return;

    const allFields = assemblyTemplate?.fields ?? [];
    return Object.entries(assemblyDetails).filter(([key]) => {
      const isDefect = templateList?.some((item) =>
        item.options.some(
          (option) =>
            option.type === OptionFieldTypeEnum.CircularProgressBar && option.fieldKey === key
        )
      );

      if (isDefect) return;
      const field = allFields.find((f) => f.key === key);
      return field?.displayInReview ?? false;
    });
  }, [assemblyDetails, assemblyTemplate?.fields, templateList]);

  const assemblyDefectsData = useMemo(() => {
    if (!assemblyDetails) return [];

    const allFields = assemblyTemplate?.fields ?? [];

    const defectMap = new Map();

    Object.entries(assemblyDetails).forEach(([key, value]) => {
      const isDefect = templateList?.some((templateItem) =>
        templateItem.options.some(
          (option) =>
            option.type === OptionFieldTypeEnum.CircularProgressBar && option.fieldKey === key
        )
      );

      if (!isDefect) return;

      const field = allFields.find((field) => field.key === key);
      if (!field?.displayInReview) return;

      const matchingTemplate = templateList?.find((templateItem) =>
        templateItem.options.some((option) => option.fieldKey === key)
      );

      const matchedOption = matchingTemplate?.options.find((option) => option.fieldKey === key);
      if (!matchedOption) return;

      const legendMatch = defectLegends?.find(
        (legend) => legend.name.toLowerCase() === matchedOption.name.toLowerCase()
      );
      if (!legendMatch) return;

      const groupKey = legendMatch.groupKey;
      const groupName = legendMatch.groupName;

      if (!defectMap.has(groupKey)) {
        defectMap.set(groupKey, {
          title: groupName,
          data: [],
        });
      }

      defectMap.get(groupKey).data.push([key, parseFloat(value)]);
    });

    return Array.from(defectMap.values());
  }, [assemblyDetails, assemblyTemplate, templateList, defectLegends]);

  return {
    assemblyGeneralData,
    assemblyDefectsData,
  };
};
